import type { ServiceLockSource } from '@avslack/shared';
import { pipSupported, useServiceSettings, useUpdateMyService } from '../data/service';
import { ApiRequestError } from '../lib/api';

/**
 * HARMAS ALLASU kapcsolo: "oroklodik" / "be" / "ki".
 *
 * A `null` ertek azt jelenti: nem allitottam, a feljebbi szint ertekere esik vissza
 * (felhasznalo > ceg > globalis). Ez NEM ugyanaz, mint a "ki": a ceg kesobbi
 * valtoztatasa az oroklodo felhasznalot kovetni fogja, a "kit" nem.
 */
export type TriValue = boolean | null;

export function TriStateField({
  label,
  hint,
  value,
  effective,
  lockedBy,
  disabled = false,
  inheritLabel,
  onChange,
}: {
  label: string;
  hint?: string;
  value: TriValue;
  /** A kesz ertek, ahogy most ervenyes (az oroklodo allas mellett kiirjuk). */
  effective?: boolean;
  /** Ha egy feljebbi szint kotelezove tette, a mezo zart. */
  lockedBy?: ServiceLockSource;
  disabled?: boolean;
  /** Mit jelent itt az "oroklodik" (alapbol: a cég / a rendszer beállítása). */
  inheritLabel?: string;
  onChange: (value: TriValue) => void;
}) {
  const locked = lockedBy !== undefined && lockedBy !== null;
  const stateText = (on: boolean) => (on ? 'be' : 'ki');
  const select = value === null ? 'inherit' : value ? 'on' : 'off';

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <div className="min-w-0 flex-1 basis-56">
        <p className="font-medium text-ink">{label}</p>
        {hint && <p className="text-ink-muted">{hint}</p>}
        {locked && (
          <p className="mt-0.5 text-[var(--danger)]">
            {lockedBy === 'team'
              ? 'A céged adminja kötelezővé tette'
              : 'A rendszergazda kötelezővé tette'}
            {effective !== undefined ? `: ${stateText(effective)}.` : '.'}
          </p>
        )}
      </div>
      <select
        value={select}
        disabled={disabled || locked}
        onChange={(event) =>
          onChange(event.target.value === 'inherit' ? null : event.target.value === 'on')
        }
        aria-label={label}
        className="rounded-control border border-border bg-surface px-2 py-1.5 text-xs disabled:opacity-50"
      >
        <option value="inherit">
          {inheritLabel ??
            `Örökli${effective !== undefined && value === null ? ` (most: ${stateText(effective)})` : ''}`}
        </option>
        <option value="on">Be</option>
        <option value="off">Ki</option>
      </select>
    </div>
  );
}

/**
 * VEVOSZOLGALAT a sajat beallitasokban (docs/20-vevoszolgalat.html).
 *
 * Aki tavoli asztalon dolgozik, annak a bejovo hivas es a hivasgombok egy lebego
 * panelben jelennek meg, a tavoli asztal FOLOTT. A kesz erteket a szerver oldja fel;
 * amit a ceg vagy a rendszergazda kotelezove tett, azt itt nem lehet atallitani.
 */
export function ServiceSection() {
  const { data } = useServiceSettings();
  const update = useUpdateMyService();
  const supported = pipSupported();

  if (!data) return null;

  const failure =
    update.error instanceof ApiRequestError
      ? update.error.message
      : update.isError
        ? 'A beállítás nem mentődött.'
        : null;

  return (
    <section className="space-y-3 border-t border-border-soft pt-4 text-xs">
      <div>
        <h3 className="font-display text-sm font-semibold">Vevőszolgálat</h3>
        <p className="mt-0.5 text-ink-muted">
          Ha távoli asztalon dolgozol, a bejövő hívás és a hívásgombok egy lebegő panelben jelennek
          meg, a távoli asztal fölött. Így nem vész el a fókusz.
        </p>
      </div>

      {!supported && (
        <p className="rounded-control bg-[var(--danger-soft)] px-3 py-2 text-ink">
          Ez a böngésző nem támogatja a lebegő panelt. Asztali Chrome vagy Edge kell hozzá, a
          beállítás itt csak előkészíthető.
        </p>
      )}

      <TriStateField
        label="Vevőszolgálati mód"
        hint="A hívás a fő ablakban fut, és a lebegő panel kezeli."
        value={data.own.enabled}
        effective={data.effective.enabled}
        lockedBy={data.lockedBy.enabled}
        onChange={(enabled) => update.mutate({ enabled })}
      />
      <TriStateField
        label="A lebegő panel magától megnyíljon"
        hint="Az első kattintásra vagy billentyűre nyílik, mert a böngésző kattintás nélkül nem engedi."
        value={data.own.autoOpen}
        effective={data.effective.autoOpen}
        lockedBy={data.lockedBy.autoOpen}
        onChange={(autoOpen) => update.mutate({ autoOpen })}
      />

      {failure && (
        <p role="alert" className="text-[var(--danger)]">
          {failure}
        </p>
      )}
    </section>
  );
}
